/**
 * CategorySummaryCards Component
 * Horizontally scrollable category spending cards
 */

import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';

export default function CategorySummaryCards({ categories = [] }) {
  const { currency: userCurrency } = useAuth();
  const currency = userCurrency || 'USD';
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const sortedCategories = categories.length > 0
    ? [...categories].sort((a, b) => (b?.amount ?? 0) - (a?.amount ?? 0))
    : [];

  // Show empty state if no categories
  if (sortedCategories.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          All Expense Categories
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No expense data yet. Add transactions or shopping lists to see your spending by category.
        </p>
      </motion.div>
    );
  }

  const displayCategories = sortedCategories;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      className="relative w-full overflow-hidden"
    >
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
          All Expense Categories
        </h2>
        <div className="hidden sm:flex gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => scroll('left')}
            className="p-1.5 sm:p-2 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-xl hover:border-[#006D77] transition-colors touch-manipulation"
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} className="text-[#006D77] sm:w-5 sm:h-5" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => scroll('right')}
            className="p-1.5 sm:p-2 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-xl hover:border-[#006D77] transition-colors touch-manipulation"
            aria-label="Scroll right"
          >
            <ChevronRight size={18} className="text-[#006D77] sm:w-5 sm:h-5" />
          </motion.button>
        </div>
      </div>

      {/* Scrollable Cards Container */}
      <div
        ref={scrollContainerRef}
        className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {displayCategories.map((category, index) => {
          const IconComponent = Icons[category.icon] || Icons.Tag;
          const percentage = category.budget ? (category.amount / category.budget) * 100 : 0;
          const isOverBudget = percentage > 100;

          return (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.4 + index * 0.05 }}
              whileHover={{ y: -4, scale: 1.02 }}
              className="flex-shrink-0 w-40 sm:w-48 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer snap-start"
            >
              {/* Icon & Name */}
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <IconComponent
                    size={16}
                    style={{ color: category.color }}
                    className="sm:w-5 sm:h-5"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm truncate">
                    {category.name}
                  </h3>
                </div>
              </div>

              {/* Amount */}
              <div className="mb-2">
                <div className="text-lg sm:text-2xl font-bold font-mono text-gray-900 dark:text-white">
                  {formatCurrency(category.amount, currency)}
                </div>
                {category.budget && (
                  <div className="text-[10px] sm:text-xs font-normal text-gray-500 dark:text-gray-400">
                    of {formatCurrency(category.budget, currency)} budget
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {category.budget && (
                <div className="space-y-1">
                  <div className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(percentage, 100)}%` }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.05 }}
                      className="h-full rounded-full"
                      style={{
                        backgroundColor: isOverBudget ? '#EF476F' : category.color
                      }}
                    />
                  </div>
                  <div className={`text-xs font-semibold ${isOverBudget ? 'text-[#EF476F]' : 'text-gray-600'}`}>
                    {percentage.toFixed(0)}% {isOverBudget ? 'over budget' : 'used'}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

    </motion.div>
  );
}
